import {useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import ModalBasic, {moleculeModalSizes} from '@adv-mt/mt-modal-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {LastCallModalContent} from './modal-content.js'
import {ERROR_TYPE, LAST_CALL_STATE, TRACKS} from './settings.js'

export default function ModalLastCall({
  adId,
  adTitle,
  dealerId,
  makeId,
  makeName = '',
  userData = {},
  onClose,
  open = false,
  vehicleId
}) {
  const {name, zipCode, userId} = userData
  const {i18n, domain} = useContext(Context)
  const {track} = useAnalyticsTrack()
  const config = domain.get('config')
  const [isLoading, setIsLoading] = useState(false)
  const [isLongDistance, setIsLongDistance] = useState(false)
  const [state, setState] = useState(LAST_CALL_STATE.INIT)
  const urlNewVehicleStaticImages = `${config.get('URL_STATICS_IMAGES')}coches/new-vehicle`
  const inputViewImageSrc = `${urlNewVehicleStaticImages}/last-call/main.svg`
  const confirmationViewImageSrc = `${urlNewVehicleStaticImages}/last-call/success.svg`
  const bestOfferViewImageSrc = `${urlNewVehicleStaticImages}/last-call/best-offer.svg`
  const dealerNotFoundImageSrc = `${urlNewVehicleStaticImages}/last-call/dealer-not-found.svg`
  const serviceUnavailableImageSrc = `${urlNewVehicleStaticImages}/last-call/service-unavailable.svg`

  const getAdvertisementsTracking = () =>
    domain.get('get_advertisements_tracking_use_case').execute({
      ads: [adId],
      context: 'other'
    })

  const trackWithProducts = ({event, postalCode, pageError}) => {
    getAdvertisementsTracking().then(({products}) =>
      track({
        event,
        data: {
          products,
          ...(postalCode && {hit_information: postalCode}),
          ...(pageError && {page_error: pageError})
        }
      })
    )
  }

  useEffect(
    () => {
      trackWithProducts({event: TRACKS.OPEN})
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [track]
  )

  const greetingsState = {
    title: i18n.t('MODAL_LAST_CALL.GREETINGS', {
      name
    }),
    text: i18n.t('MODAL_LAST_CALL.MESSAGE', {makeName}),
    image: {
      src: inputViewImageSrc
    }
  }

  const MODAL_STATES = {
    [LAST_CALL_STATE.INIT]: greetingsState,
    [LAST_CALL_STATE.CONFIRMED]: {
      title: i18n.t('MODAL_LAST_CALL.CONFIRMATION_TITLE'),
      text: i18n.t('MODAL_LAST_CALL.CONFIRMATION_MESSAGE'),
      image: {
        src: confirmationViewImageSrc
      }
    },
    [LAST_CALL_STATE.DEALER_ALREADY_CONTACTED]: {
      title: i18n.t('MODAL_LAST_CALL.ERRORS.BEST_OFFER.TITLE', {
        name
      }),
      text: i18n.t('MODAL_LAST_CALL.ERRORS.BEST_OFFER.MESSAGE', {
        makeName
      }),
      image: {
        src: bestOfferViewImageSrc
      }
    },
    [LAST_CALL_STATE.DEALER_NOT_FOUND]: {
      title: i18n.t('MODAL_LAST_CALL.ERRORS.DEALER_NOT_FOUND.TITLE', {
        name
      }),
      text: i18n.t('MODAL_LAST_CALL.ERRORS.DEALER_NOT_FOUND.MESSAGE', {
        makeName
      }),
      image: {
        src: dealerNotFoundImageSrc
      }
    },
    [LAST_CALL_STATE.SERVICE_UNAVAILABLE]: {
      title: i18n.t('MODAL_LAST_CALL.ERRORS.SERVICE_UNAVAILABLE.TITLE', {
        name
      }),
      text: i18n.t('MODAL_LAST_CALL.ERRORS.SERVICE_UNAVAILABLE.MESSAGE', {
        makeName
      }),
      image: {
        src: serviceUnavailableImageSrc
      }
    }
  }

  const modalProps = MODAL_STATES[state]

  const _handleLastCallError = ({error, postalCode}) => {
    switch (error.errorType) {
      case ERROR_TYPE.DEALER_NOT_FOUND:
        setState(LAST_CALL_STATE.DEALER_NOT_FOUND)
        break
      case ERROR_TYPE.ORIGIN_NOT_SUITABLE_FOR_LC:
      case ERROR_TYPE.DEALER_ALREADY_CONTACTED:
      case ERROR_TYPE.LASTCALL_ALREADY_EXISTS:
        setState(LAST_CALL_STATE.DEALER_ALREADY_CONTACTED)
        break
      case ERROR_TYPE.INVALID_USER:
      default:
        setState(LAST_CALL_STATE.SERVICE_UNAVAILABLE)
        break
    }
    trackWithProducts({
      event: TRACKS.FAILED,
      postalCode,
      pageError: error.errorType
    })
    return console.error(error)
  }

  const createLastCall = async createLastCallParams => {
    const [error, response] = await domain.get('create_last_call_use_case').execute(createLastCallParams)
    const {postalCode} = createLastCallParams
    setIsLoading(false)
    if (error) {
      return _handleLastCallError({error, postalCode})
    }
    trackWithProducts({event: TRACKS.SUCCESS, postalCode})
    setIsLongDistance(response.isLongDistance)
    setState(LAST_CALL_STATE.CONFIRMED)
  }

  const onConfirm = postalCode => {
    setIsLoading(true)
    createLastCall({
      adId,
      dealerId,
      makeId,
      postalCode,
      title: adTitle,
      uniqueVehicleId: vehicleId,
      userId
    })
  }

  return (
    <ModalBasic isOpen={open} onClose={onClose} size={moleculeModalSizes.LARGE}>
      <LastCallModalContent
        {...modalProps}
        zipCode={zipCode}
        adId={adId}
        onConfirm={onConfirm}
        onClose={onClose}
        isLoading={isLoading}
        isLongDistance={isLongDistance}
        showForm={state === LAST_CALL_STATE.INIT}
      />
    </ModalBasic>
  )
}

ModalLastCall.displayName = 'ModalLastCall'
ModalLastCall.propTypes = {
  adId: PropTypes.string,
  adTitle: PropTypes.string,
  dealerId: PropTypes.string,
  makeId: PropTypes.string,
  makeName: PropTypes.string,
  userData: PropTypes.shape({
    name: PropTypes.string,
    zipCode: PropTypes.string
  }),
  onClose: PropTypes.func,
  open: PropTypes.bool,
  vehicleId: PropTypes.string
}
